import type { AppLanguage, AppTheme, IUserPreferences } from "../types";

export const DEFAULT_THEME: AppTheme = "light";

export const normalizeAppLanguage = (
  language: string | null | undefined,
): AppLanguage | undefined => {
  const normalized = language?.trim().toLowerCase();
  if (!normalized) return undefined;
  if (
    normalized === "no" ||
    normalized === "nb" ||
    normalized === "nn" ||
    normalized.startsWith("no-") ||
    normalized.startsWith("nb-") ||
    normalized.startsWith("nn-")
  ) {
    return "no";
  }
  if (normalized === "en" || normalized.startsWith("en-")) {
    return "en";
  }
  return undefined;
};

export const normalizeAppTheme = (
  theme: string | null | undefined,
): AppTheme | undefined =>
  theme === "dark" || theme === "light" ? theme : undefined;

const browserStorage = (): Storage | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const selectedLanguageKey = (email: string) =>
  `logibud:selectedLanguage:${email.trim().toLowerCase()}`;

/** Unlike i18next's detection cache, this records only an explicit choice. */
export const rememberSelectedLanguage = (
  language: AppLanguage,
  email?: string,
  storage: Storage | undefined = browserStorage(),
): void => {
  try {
    if (email) storage?.setItem(selectedLanguageKey(email), language);
    storage?.setItem("i18nextLng", language);
  } catch {
    // Language changes still work if the browser blocks persistent storage.
  }
};

const readSelectedLanguage = (email?: string, storage = browserStorage()) => {
  try {
    return email
      ? normalizeAppLanguage(storage?.getItem(selectedLanguageKey(email)))
      : undefined;
  } catch {
    return undefined;
  }
};

export const readCachedUserPreferences = (
  storage: Storage | undefined = browserStorage(),
): IUserPreferences => {
  if (!storage) return { theme: DEFAULT_THEME };

  const language = normalizeAppLanguage(storage.getItem("i18nextLng"));
  const storedDarkMode = storage.getItem("darkMode");
  const theme =
    storedDarkMode === "true"
      ? "dark"
      : storedDarkMode === "false"
        ? "light"
        : DEFAULT_THEME;

  return {
    ...(language ? { language } : {}),
    theme,
  };
};

export const resolveUserPreferences = (
  profilePreferences: IUserPreferences | null | undefined,
  storage?: Storage,
  email?: string,
): IUserPreferences => {
  const cached = readCachedUserPreferences(storage);
  const language =
    readSelectedLanguage(email, storage) ??
    normalizeAppLanguage(profilePreferences?.language) ??
    cached.language;
  const theme =
    normalizeAppTheme(profilePreferences?.theme) ??
    cached.theme ??
    DEFAULT_THEME;

  return {
    ...(language ? { language } : {}),
    theme,
  };
};

export const userPreferencesEqual = (
  left: IUserPreferences | null | undefined,
  right: IUserPreferences,
): boolean =>
  normalizeAppLanguage(left?.language) === right.language &&
  normalizeAppTheme(left?.theme) === right.theme;
